/**
 * Billing API client (web). Typed wrappers for the multi-provider Billing module
 * endpoints (`/api/v1/billing/*`) that drive the checkout + plan-management
 * journey. Read-only subscription state (status/plans/usage) lives in
 * subscriptionApi.ts and stays on the un-versioned `/api/subscription/*` routes.
 *
 * Provider-agnostic by design: checkout returns a hosted `redirectUrl` (Stripe
 * today, WayForPay later) and the outcome is reflected back via the subscription
 * `status` query (webhook-driven), so plan-changing calls should invalidate it
 * rather than parse provider-specific return params.
 */
import { apiClient } from '../../../services/apiClient';

export type BillingCycle = 'monthly' | 'annual';

export interface CheckoutSessionResponse {
  sessionId: string;
  /** Provider-hosted payment page; redirect the browser here. */
  redirectUrl: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface BillingCustomerResponse {
  organizationId: string;
  /** 'stripe' | 'wayforpay'. */
  providerType: string;
  /** 'EUR' | 'UAH'. */
  currency: string;
  providerCustomerId: string;
  /** Null until the org has an active paid subscription (drives checkout-vs-upgrade branching). */
  providerSubscriptionId: string | null;
}

export interface CreateCheckoutRequest {
  plan: string;
  billingCycle: BillingCycle;
  /** Absolute return URLs; backend defaults to /billing/success|cancel when omitted. */
  successUrl?: string;
  cancelUrl?: string;
}

export interface PlanChangeRequest {
  plan: string;
  billingCycle: BillingCycle;
}

/** POST /api/v1/billing/checkout → hosted checkout session; redirect to `redirectUrl`. */
export const createCheckout = (body: CreateCheckoutRequest) =>
  apiClient.post<CheckoutSessionResponse>('/api/v1/billing/checkout', body);

/** POST /api/v1/billing/portal → provider-hosted management session (Stripe only today). */
export const createBillingPortal = (returnUrl?: string) =>
  apiClient.post<PortalSessionResponse>('/api/v1/billing/portal', { returnUrl });

/** GET /api/v1/billing/customer → provider + subscription linkage for the current org. */
export const getBillingCustomer = () =>
  apiClient.get<BillingCustomerResponse>('/api/v1/billing/customer');

/** POST /api/v1/billing/upgrade → immediate prorated upgrade (requires an active paid sub). */
export const upgradePlan = (body: PlanChangeRequest) =>
  apiClient.post<{ message: string }>('/api/v1/billing/upgrade', body);

/** POST /api/v1/billing/downgrade → schedule a downgrade at period end (requires an active paid sub). */
export const downgradePlan = (body: PlanChangeRequest) =>
  apiClient.post<{ message: string }>('/api/v1/billing/downgrade', body);

/** POST /api/v1/billing/cancel-downgrade → cancel a pending downgrade. */
export const cancelPendingDowngrade = () =>
  apiClient.post<{ message: string }>('/api/v1/billing/cancel-downgrade');

/** POST /api/v1/billing/cancel → cancel at the end of the current billing period. */
export const cancelSubscription = () =>
  apiClient.post<{ message: string }>('/api/v1/billing/cancel');

/** POST /api/v1/billing/reactivate → reverse a pending cancellation. */
export const reactivateSubscription = () =>
  apiClient.post<{ message: string }>('/api/v1/billing/reactivate');
